import { useState } from "react";

interface ShareStrategyModalProps {
  isOpen: boolean;
  onClose: () => void;
  strategyId: string;
  strategyName: string;
  metrics?: {
    cagr: number;
    sharpe: number;
    maxDrawdown: number;
  };
}

export function ShareStrategyModal({
  isOpen,
  onClose,
  strategyId,
  strategyName,
  metrics,
}: ShareStrategyModalProps) {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const shareUrl = `${window.location.origin}/backtest/${strategyId}`;

  // Create share text with metrics if available
  const shareText = metrics
    ? `Check out "${strategyName}" trading strategy! 📈\n\nCAGR: ${(metrics.cagr * 100).toFixed(1)}%\nSharpe: ${metrics.sharpe.toFixed(2)}\nMax Drawdown: ${(metrics.maxDrawdown * 100).toFixed(1)}%`
    : `Check out "${strategyName}" trading strategy!`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleShare = (platform: string) => {
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedText = encodeURIComponent(shareText);

    const shareUrls: Record<string, string> = {
      twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      reddit: `https://reddit.com/submit?url=${encodedUrl}&title=${encodeURIComponent(strategyName)}`,
      telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
      whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      email: `mailto:?subject=${encodeURIComponent(`Trading Strategy: ${strategyName}`)}&body=${encodedText}%0A%0A${encodedUrl}`,
    };

    if (shareUrls[platform]) {
      window.open(shareUrls[platform], "_blank", "width=600,height=400");
    }
  };

  const socialPlatforms = [
    {
      id: "twitter",
      name: "X (Twitter)",
      icon: "𝕏",
      color: "bg-black hover:bg-gray-800",
    },
    {
      id: "facebook",
      name: "Facebook",
      icon: "f",
      color: "bg-[#1877F2] hover:bg-[#1565D8]",
    },
    {
      id: "linkedin",
      name: "LinkedIn",
      icon: "in",
      color: "bg-[#0A66C2] hover:bg-[#0955A5]",
    },
    {
      id: "reddit",
      name: "Reddit",
      icon: "👽",
      color: "bg-[#FF4500] hover:bg-[#E03D00]",
    },
    {
      id: "telegram",
      name: "Telegram",
      icon: "✈️",
      color: "bg-[#0088CC] hover:bg-[#0077B3]",
    },
    {
      id: "whatsapp",
      name: "WhatsApp",
      icon: "💬",
      color: "bg-[#25D366] hover:bg-[#20BD5A]",
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-surface-900 border border-surface-700 rounded-2xl shadow-2xl w-full max-w-md animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-surface-700">
          <h2 className="text-xl font-semibold text-surface-100">
            Share Strategy
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-surface-800 transition-colors"
          >
            <svg
              className="w-5 h-5 text-surface-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Strategy Preview */}
          <div className="bg-surface-800/50 rounded-xl p-4 border border-surface-700/50">
            <h3 className="font-semibold text-surface-100 mb-2">
              {strategyName}
            </h3>
            {metrics && (
              <div className="flex gap-4 text-sm">
                <span
                  className={
                    metrics.cagr >= 0 ? "text-emerald-400" : "text-red-400"
                  }
                >
                  CAGR: {(metrics.cagr * 100).toFixed(1)}%
                </span>
                <span className="text-surface-400">
                  Sharpe: {metrics.sharpe.toFixed(2)}
                </span>
                <span className="text-red-400">
                  Max DD: {(metrics.maxDrawdown * 100).toFixed(1)}%
                </span>
              </div>
            )}
          </div>

          {/* Copy Link */}
          <div>
            <label className="block text-sm font-medium text-surface-400 mb-2">
              Strategy Link
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={shareUrl}
                readOnly
                className="input-field flex-1 text-sm bg-surface-800"
              />
              <button
                onClick={handleCopyLink}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  copied
                    ? "bg-emerald-500 text-white"
                    : "bg-surface-700 text-surface-200 hover:bg-surface-600"
                }`}
              >
                {copied ? (
                  <span className="flex items-center gap-1">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    Copied!
                  </span>
                ) : (
                  "Copy"
                )}
              </button>
            </div>
          </div>

          {/* Social Sharing */}
          <div>
            <label className="block text-sm font-medium text-surface-400 mb-3">
              Share on Social Media
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {socialPlatforms.map((platform) => (
                <button
                  key={platform.id}
                  onClick={() => handleShare(platform.id)}
                  className={`${platform.color} text-white rounded-xl p-3 flex flex-col items-center justify-center gap-1 transition-all transform hover:scale-105`}
                >
                  <span className="text-lg font-bold">{platform.icon}</span>
                  <span className="text-xs">{platform.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Email Share */}
          <button
            onClick={() => handleShare("email")}
            className="w-full flex items-center justify-center gap-2 bg-surface-700 hover:bg-surface-600 text-surface-200 rounded-xl p-3 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            Share via Email
          </button>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-surface-700 bg-surface-800/30 rounded-b-2xl">
          <p className="text-xs text-surface-500 text-center">
            Anyone with the link can view this strategy and run backtests
          </p>
        </div>
      </div>
    </div>
  );
}
